import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import TrafficLights from "../TrafficLights";
import WindowFrame from "../WindowFrame";
import useGenie from "../../hooks/useGenie";
import { notify } from "../Notification";
import { CONTAINERS, IMAGES, VOLUMES } from "../../data/orbstack";
import { formatSize, imageFor } from "./shared";
import { ContainersList, ImagesList, NoPods, VolumesList } from "./lists";
import { ContainerInfo, ImageInfo, VolumeInfo } from "./info";
import { ReactComponent as ContainersIcon } from "../../assets/svgs/sf-symbols/orbstack/shippingbox.svg";
import { ReactComponent as VolumesIcon } from "../../assets/svgs/sf-symbols/orbstack/externaldrive.svg";
import { ReactComponent as ImagesIcon } from "../../assets/svgs/sf-symbols/orbstack/zipper.page.svg";
import { ReactComponent as NetworksIcon } from "../../assets/svgs/sf-symbols/orbstack/point.3.filled.connected.trianglepath.dotted.svg";
import { ReactComponent as PodsIcon } from "../../assets/svgs/sf-symbols/orbstack/helm.svg";
import { ReactComponent as ServicesIcon } from "../../assets/svgs/sf-symbols/orbstack/network.svg";
import { ReactComponent as MachinesIcon } from "../../assets/svgs/sf-symbols/orbstack/display.svg";
import { ReactComponent as ActivityIcon } from "../../assets/svgs/sf-symbols/orbstack/chart.xyaxis.line.svg";
import { ReactComponent as CommandsIcon } from "../../assets/svgs/sf-symbols/orbstack/apple.terminal.svg";
import { ReactComponent as PersonIcon } from "../../assets/svgs/sf-symbols/orbstack/person.crop.circle.svg";
import { ReactComponent as SidebarIcon } from "../../assets/svgs/sf-symbols/orbstack/sidebar.left.svg";
import { ReactComponent as SortIcon } from "../../assets/svgs/sf-symbols/orbstack/arrow.up.arrow.down.svg";
import { ReactComponent as FilterIcon } from "../../assets/svgs/sf-symbols/orbstack/line.3.horizontal.decrease.circle.svg";
import { ReactComponent as ChevronDown } from "../../assets/svgs/sf-symbols/orbstack/chevron.down.svg";
import { ReactComponent as SearchIcon } from "../../assets/svgs/sf-symbols/orbstack/magnifyingglass.svg";
import { ReactComponent as PlusIcon } from "../../assets/svgs/sf-symbols/orbstack/plus.svg";
import { ReactComponent as OpenIcon } from "../../assets/svgs/sf-symbols/orbstack/arrow.up.forward.square.svg";

const SIDEBAR = [
  {
    title: "Docker",
    items: [
      ["Containers", ContainersIcon],
      ["Volumes", VolumesIcon],
      ["Images", ImagesIcon],
      ["Networks", NetworksIcon],
    ],
  },
  {
    title: "Kubernetes",
    items: [
      ["Pods", PodsIcon],
      ["Services", ServicesIcon],
    ],
  },
  { title: "Linux", items: [["Machines", MachinesIcon]] },
  {
    title: "General",
    items: [
      ["Activity Monitor", ActivityIcon],
      ["Commands", CommandsIcon],
    ],
  },
];

// The sections that open, with the tabs over their details and their list's sort menu icon.
const VIEWS = {
  Containers: { tabs: ["Info", "Logs", "Terminal", "Files"], sort: FilterIcon },
  Volumes: { tabs: ["Info", "Files"], sort: SortIcon },
  Images: { tabs: ["Info", "Terminal", "Files"], sort: SortIcon },
  Pods: { tabs: ["Info", "Logs", "Terminal"], sort: FilterIcon, kubernetes: true },
};

// The projects open when the window does, as in Sylvester's screenshot.
const EXPANDED = { rhodes: true, lantern: true };

// Default frame: up to 1180 x 700, a little left of center, so its sides show around Notes.
const orbFrame = (ws) => {
  const w = Math.min(1180, ws.w - 80);
  const h = Math.min(700, ws.h - 32);
  return { x: (ws.w - w) / 2 - 40, y: (ws.h - h) / 2, w, h };
};

const matches = (q, ...texts) => !q || texts.some((t) => t && t.toLowerCase().includes(q));

// OrbStack, like Sylvester's: his containers, volumes and images, each with its details beside it,
// and Kubernetes with no pods. Containers can be started, stopped and deleted, and what they use
// follows: an image or volume no container uses moves to Unused, and can be deleted. It all comes
// back when the window is closed.
const OrbStackWindow = ({ workspace, open, front, onFocus, onClose }) => {
  const [exit, setExit] = useState("closed");
  const genie = useGenie({ app: "OrbStack", open, exit });
  const [maximized, setMaximized] = useState(false);
  const [section, setSection] = useState("Containers");
  const [containers, setContainers] = useState(CONTAINERS);
  const [images, setImages] = useState(IMAGES);
  const [volumes, setVolumes] = useState(VOLUMES);
  const [selected, setSelected] = useState({}); // per section
  const [expanded, setExpanded] = useState(EXPANDED);
  const [sidebar, setSidebar] = useState(true);
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState("");
  const [stuck, setStuck] = useState({}); // section headers pinned to the top of the list
  const [reveal, setReveal] = useState(null); // a row to scroll to once it's shown
  const scroller = useRef(null);
  const searchField = useRef(null);
  const view = VIEWS[section];

  // What uses what: an image's containers, a volume's containers.
  const q = query.trim().toLowerCase();
  const imageRows = images
    .map((image) => ({
      ...image,
      users: containers.filter((c) => (imageFor(images, c.image) || {}).ref === image.ref),
    }))
    .filter((i) => matches(q, i.ref));
  const volumeRows = [...volumes]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((volume) => ({ ...volume, users: containers.filter((c) => c.mounts.some(([name]) => name === volume.name)) }))
    .filter((v) => matches(q, v.name));
  const containerRows = containers.filter((c) => matches(q, c.name, c.project, c.image));

  const subtitle = {
    Containers: `${containers.filter((c) => c.running).length} running`,
    Volumes: `${formatSize(volumes.reduce((sum, v) => sum + v.size, 0))} total`,
    Images: `${formatSize(images.reduce((sum, i) => sum + i.size, 0))} total`,
  }[section];

  // A section header shows its rule only while it's pinned over the list.
  const measureStuck = () => {
    const el = scroller.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    const next = {};
    el.querySelectorAll(".ob-section-title").forEach((h) => {
      next[h.dataset.title] = h.getBoundingClientRect().top - top < 0.5;
    });
    setStuck((s) =>
      Object.keys(next).length === Object.keys(s).length && Object.keys(next).every((k) => s[k] === next[k]) ? s : next,
    );
  };
  useLayoutEffect(measureStuck);

  useEffect(() => {
    if (searching && searchField.current) searchField.current.focus();
  }, [searching]);

  // Bring a row into view after showing it from another section.
  useLayoutEffect(() => {
    if (!reveal || !scroller.current) return;
    const row = scroller.current.querySelector(`[data-id="${CSS.escape(reveal)}"]`);
    if (row) row.scrollIntoView({ block: "center" });
    setReveal(null);
  }, [reveal]);

  const select = (key) => setSelected((s) => ({ ...s, [section]: key }));

  const go = (to) => {
    setSection(to);
    setSearching(false);
    setQuery("");
  };

  const show = (to, key, project) => {
    go(to);
    setSelected((s) => ({ ...s, [to]: key }));
    if (project) setExpanded((e) => ({ ...e, [project]: true }));
    setReveal(key);
  };

  const hide = (how) => {
    setExit(how);
    if (how === "closed") {
      setMaximized(false);
      setSection("Containers");
      setContainers(CONTAINERS);
      setImages(IMAGES);
      setVolumes(VOLUMES);
      setSelected({});
      setExpanded(EXPANDED);
      setSearching(false);
      setQuery("");
    }
    onClose();
  };

  const run = (list, running) => {
    const ids = list.map((c) => c.id);
    setContainers((cs) => cs.map((c) => (ids.includes(c.id) ? { ...c, running } : c)));
  };

  const removeContainers = (list) => {
    const ids = list.map((c) => c.id);
    setContainers((cs) => cs.filter((c) => !ids.includes(c.id)));
    if (ids.includes(selected.Containers)) select(null);
  };

  const removeImage = (image) => {
    setImages((list) => list.filter((i) => i.ref !== image.ref));
    if (selected.Images === image.ref) select(null);
  };

  const removeVolume = (volume) => {
    setVolumes((list) => list.filter((v) => v.name !== volume.name));
    if (selected.Volumes === volume.name) select(null);
  };

  // Up and down move the selection, as in a macOS list.
  const onListKey = (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const rows = scroller.current ? [...scroller.current.querySelectorAll("[role=option]")] : [];
    if (!rows.length) return;
    e.preventDefault();
    const at = rows.findIndex((r) => r.dataset.id === selected[section]);
    const next = at < 0 ? 0 : Math.max(0, Math.min(rows.length - 1, at + (e.key === "ArrowDown" ? 1 : -1)));
    select(rows[next].dataset.id);
    rows[next].scrollIntoView({ block: "nearest" });
  };

  const showContainer = (c) => show("Containers", c.id, c.project);

  const details = () => {
    const key = selected[section];
    if (section === "Images") {
      const image = imageRows.find((i) => i.ref === key) || images.find((i) => i.ref === key);
      if (image)
        return (
          <ImageInfo
            image={image}
            images={images}
            usedBy={containers.filter((c) => (imageFor(images, c.image) || {}).ref === image.ref)}
            onShowContainer={showContainer}
          />
        );
    }
    if (section === "Containers") {
      const container = containers.find((c) => c.id === key);
      if (container)
        return (
          <ContainerInfo
            container={container}
            images={images}
            volumes={volumes}
            image={imageFor(images, container.image)}
            onShowImage={(image) => show("Images", image.ref)}
            onShowVolume={(name) => volumes.some((v) => v.name === name) && show("Volumes", name)}
          />
        );
    }
    if (section === "Volumes") {
      const volume = volumes.find((v) => v.name === key);
      if (volume)
        return (
          <VolumeInfo
            volume={volume}
            images={images}
            users={containers.filter((c) => c.mounts.some(([name]) => name === volume.name))}
            onShowContainer={showContainer}
          />
        );
    }
    return <p className="ob-empty">No Selection</p>;
  };

  const listProps = { selected: selected[section], onSelect: select, stuck };
  const list = {
    Containers: (
      <ContainersList
        containers={containerRows}
        images={images}
        expanded={expanded}
        searching={Boolean(q)}
        onToggle={(project) => setExpanded((e) => ({ ...e, [project]: !e[project] }))}
        onRun={run}
        onDelete={removeContainers}
        {...listProps}
      />
    ),
    Volumes: <VolumesList volumes={volumeRows} onDelete={removeVolume} {...listProps} />,
    Images: <ImagesList images={imageRows} onDelete={removeImage} {...listProps} />,
  }[section];
  const empty =
    q && !(section === "Containers" ? containerRows : section === "Volumes" ? volumeRows : imageRows).length;

  const state = open ? "nw--open" : `nw--${exit}`;
  return (
    <WindowFrame
      workspace={workspace}
      initial={orbFrame}
      min={{ w: 760, h: 420 }}
      titlebar={52}
      open={open}
      front={front}
      zoomed={maximized}
      onFocus={onFocus}
      onToggleZoom={() => setMaximized((m) => !m)}
    >
      <div
        role="dialog"
        aria-label="OrbStack"
        aria-hidden={!open}
        ref={genie.ref}
        className={`nw ob ${state} ${maximized ? "nw--max" : ""} ${front ? "is-front" : ""} ${
          sidebar ? "" : "is-collapsed"
        } ${genie.hidden ? "is-genie-hidden" : ""}`}
      >
        <div className="traffic">
          <TrafficLights
            onClose={() => hide("closed")}
            onMinimize={() => genie.minimize(() => hide("minimized"))}
            onPrepare={genie.prepare}
            onZoom={() => setMaximized(!maximized)}
            zoomed={maximized}
          />
        </div>
        <button
          type="button"
          className="ob-sidebar-toggle"
          onClick={() => setSidebar((s) => !s)}
          aria-label={sidebar ? "Hide Sidebar" : "Show Sidebar"}
        >
          <SidebarIcon />
        </button>

        <aside className="ob-sidebar no-select" aria-hidden={!sidebar}>
          <nav className="ob-nav" aria-label="OrbStack">
            {SIDEBAR.map((group) => (
              <div key={group.title} className="ob-nav-group">
                <h3 className="ob-nav-title">{group.title}</h3>
                {group.items.map(([label, Icon]) => (
                  <button
                    type="button"
                    key={label}
                    className={`ob-nav-item ${label === section ? "is-selected" : ""}`}
                    aria-current={label === section ? "page" : undefined}
                    tabIndex={sidebar ? 0 : -1}
                    onClick={() =>
                      VIEWS[label]
                        ? go(label)
                        : notify(`${label} isn't in this OrbStack yet. Try Containers, Volumes or Images.`)
                    }
                  >
                    <Icon className="ob-nav-icon" aria-hidden="true" />
                    {label}
                  </button>
                ))}
              </div>
            ))}
          </nav>
          <span className="ob-signin" aria-hidden="true">
            <PersonIcon className="ob-signin-icon" />
            Sign In
          </span>
        </aside>

        <section className={`ob-list ${view.kubernetes ? "ob-list--empty" : ""}`} aria-label={section}>
          <header className={`ob-list-head no-select ${subtitle ? "" : "is-single"}`}>
            <div className="ob-list-titles">
              <h2 className="ob-list-title">{section}</h2>
              {subtitle && <p className="ob-list-sub">{subtitle}</p>}
            </div>
            <span className="ob-sort" aria-hidden="true">
              <view.sort className={view.sort === SortIcon ? "ob-sym-sort" : "ob-sym-filter"} />
              <ChevronDown className="ob-sym-chevron" />
            </span>
            <button
              type="button"
              className="ob-tool"
              onClick={() => {
                setSearching((s) => !s);
                setQuery("");
              }}
              aria-label="Search"
              aria-pressed={searching}
            >
              <SearchIcon className="ob-sym-search" />
            </button>
          </header>
          {searching && (
            <div className="ob-search">
              <SearchIcon className="ob-search-icon" aria-hidden="true" />
              <input
                ref={searchField}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setSearching(false);
                    setQuery("");
                  }
                }}
                placeholder="Search"
                aria-label={`Search ${section.toLowerCase()}`}
                spellCheck={false}
              />
            </div>
          )}
          {view.kubernetes ? (
            <NoPods />
          ) : (
            <div
              key={section}
              className={`ob-scroll ob-scroll--${section.toLowerCase()}`}
              ref={scroller}
              onScroll={measureStuck}
              role="listbox"
              aria-label={section}
              tabIndex={0}
              onKeyDown={onListKey}
            >
              {list}
              {empty && <p className="ob-none">No Results</p>}
            </div>
          )}
        </section>

        <section className="ob-detail" aria-label="Details">
          <div className="ob-toolbar no-select" data-tabs={view.tabs.length}>
            {view.kubernetes ? (
              <span className="ob-switch is-on" aria-hidden="true" />
            ) : (
              <span className="ob-tool" aria-hidden="true">
                <PlusIcon className="ob-sym-plus" />
              </span>
            )}
            <div className="ob-segments" role="tablist" aria-label="View">
              {view.tabs.map((tab) => (
                <button
                  type="button"
                  key={tab}
                  role="tab"
                  aria-selected={tab === "Info"}
                  className={`ob-segment ${tab === "Info" ? "is-selected" : ""}`}
                  onClick={() =>
                    tab !== "Info" && notify(`${tab} needs Docker running on my Mac, so this replica shows Info.`)
                  }
                >
                  {tab}
                </button>
              ))}
            </div>
            <span className="ob-pill" aria-hidden="true">
              Personal use only
            </span>
            <span className="ob-open" aria-hidden="true">
              <OpenIcon />
            </span>
          </div>
          <div className="ob-detail-body">{details()}</div>
        </section>
      </div>
    </WindowFrame>
  );
};

export default OrbStackWindow;
